<template>
  <header class="header" :class="{ 'header--scrolled': isScrolled }">
    <div class="container header__inner">
      <button type="button" class="header__logo" @click="goTo('home')">
        <span class="header__logo-icon">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <circle cx="14" cy="14" r="12" stroke="#04213b" stroke-width="2" fill="none"/>
            <path d="M8 14c0-3.3 2.7-8 6-8s6 4.7 6 8-2.7 8-6 8" stroke="#04213b" stroke-width="2" fill="none"/>
          </svg>
        </span>
        <span class="header__logo-text">{{ SITE.shortName }}</span>
      </button>

      <nav v-if="!mobile" class="header__nav">
        <button
          v-for="item in NAV_ITEMS"
          :key="item.id"
          type="button"
          class="header__link"
          :class="{ 'header__link--active': activeSection === item.id }"
          @click="goTo(item.id)"
        >
          {{ item.label }}
        </button>
      </nav>

      <div class="header__actions">
        <div v-if="!mobile" class="header__socials">
          <a
            v-for="link in SOCIAL_LINKS.slice(0, 4)"
            :key="link.icon"
            :href="link.url"
            class="header__social-btn"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="link.label"
          >
            <v-icon size="16">{{ link.icon }}</v-icon>
          </a>
        </div>
        <button
          type="button"
          class="header__menu-btn"
          aria-label="Menu"
          @click="drawerOpen = true"
        >
          <v-icon size="20">mdi-menu</v-icon>
        </button>
      </div>
    </div>

    <v-navigation-drawer
      v-model="drawerOpen"
      location="right"
      temporary
      width="300"
      class="mobile-nav"
    >
      <div class="mobile-nav__head">
        <span class="header__logo-text">{{ SITE.shortName }}</span>
        <button type="button" class="mobile-nav__close" @click="drawerOpen = false">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>
      <nav class="mobile-nav__links">
        <button
          v-for="item in NAV_ITEMS"
          :key="item.id"
          type="button"
          class="mobile-nav__link"
          @click="goTo(item.id)"
        >
          {{ item.label }}
        </button>
      </nav>
      <div class="mobile-nav__socials">
        <a
          v-for="link in SOCIAL_LINKS"
          :key="link.icon"
          :href="link.url"
          class="header__social-btn"
          target="_blank"
          rel="noopener noreferrer"
        >
          <v-icon size="16">{{ link.icon }}</v-icon>
        </a>
      </div>
    </v-navigation-drawer>
  </header>
</template>

<script setup>
import { ref, computed } from "vue";
import { useDisplay } from "vuetify";
import { SITE, NAV_ITEMS, SOCIAL_LINKS } from "@/constants/site";
import { useNavigation } from "@/composables/useNavigation";
import { useScrollSpy } from "@/composables/useScrollSpy";

const { mdAndDown } = useDisplay();
const mobile = computed(() => mdAndDown.value);

const drawerOpen = ref(false);
const sectionIds = ["home", "about", "features", "skills", "portfolio", "resume", "contact"];
const { activeSection, isScrolled } = useScrollSpy(sectionIds);
const { navigateToSection } = useNavigation();

const goTo = (id) => {
  drawerOpen.value = false;
  navigateToSection(id);
};
</script>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 200;
  height: var(--header-height);
  transition: background var(--transition), backdrop-filter var(--transition);
}

.header--scrolled {
  background: rgba(12, 12, 12, 0.92);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--color-border);
}

.header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.header__logo {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.header__logo-text {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.02em;
}

.header__nav {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.header__link {
  background: none;
  border: none;
  font-family: var(--font-sans);
  font-size: 0.875rem;
  font-weight: 500;
  color: #cccccc;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: color var(--transition);
}

.header__link:hover,
.header__link--active {
  color: var(--color-text);
  text-shadow: 0 0 12px rgba(var(--color-accent-rgb), 0.35);
}

.header__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header__socials {
  display: flex;
  gap: 0.5rem;
}

.header__social-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-full);
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  transition: all var(--transition);
}

.header__social-btn:hover {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: var(--color-text);
}

.header__menu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-full);
  background: var(--color-accent);
  border: none;
  color: var(--color-text);
  cursor: pointer;
  transition: transform var(--transition), box-shadow var(--transition);
}

.header__menu-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 20px var(--color-accent-glow);
}

.mobile-nav {
  background: var(--color-surface) !important;
}

.mobile-nav__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-border);
}

.mobile-nav__close {
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.mobile-nav__links {
  display: flex;
  flex-direction: column;
  padding: 1rem 1.5rem;
}

.mobile-nav__link {
  background: none;
  border: none;
  text-align: left;
  font-family: var(--font-sans);
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-text-muted);
  padding: 0.875rem 0;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  transition: color var(--transition);
}

.mobile-nav__link:hover {
  color: var(--color-accent);
}

.mobile-nav__socials {
  display: flex;
  gap: 0.5rem;
  padding: 1.5rem;
}
</style>
